import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import {
  Edit,
  Trash2,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { getProductTypeLabel } from "@/features/products/utils/product.utils";
import { memo } from "react";

interface ProductsTableProps {
  products: ProductResponseDTO[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (product: ProductResponseDTO) => void;
  onDelete: (product: ProductResponseDTO) => void;
  onViewImage: (product: ProductResponseDTO) => void;
  onPageChange: (page: number) => void;
}

export const ProductsTable = memo(function ProductsTable({
  products,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onDelete,
  onViewImage,
  onPageChange,
}: ProductsTableProps) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tipo</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Marca</TableHead>
            <TableHead>Cor</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead>Foto</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => (
            <TableRow key={product.id}>
              <TableCell>
                <Badge variant="outline">
                  {getProductTypeLabel(product.type)}
                </Badge>
              </TableCell>
              <TableCell className="font-medium">
                {product.name || "-"}
              </TableCell>
              <TableCell>{product.brand}</TableCell>
              <TableCell>
                <Badge variant="secondary">{product.color}</Badge>
              </TableCell>
              <TableCell className="text-muted-foreground max-w-xs truncate">
                {product.description || "-"}
              </TableCell>
              <TableCell>
                {product.photoUrl ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onViewImage(product)}
                    title="Ver foto"
                    disabled={loading}
                  >
                    <ImageIcon className="w-4 h-4 text-primary" />
                  </Button>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Can permission={ACTIONS.products.update}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onEdit(product)}
                      title="Editar"
                      disabled={loading}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Can>
                  <Can permission={ACTIONS.products.delete}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onDelete(product)}
                      title="Excluir"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </Can>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            {/* Páginas */}
            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                // Mostrar apenas páginas próximas à atual
                const showPage =
                  i === 0 || // primeira
                  i === totalPages - 1 || // última
                  (i >= currentPage - 1 && i <= currentPage + 1); // próximas

                // Mostrar reticências
                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
